import { ApolloProvider } from "@apollo/client/react";
import { AuthenticationContext } from "@atomic/app-shell";
import { useContext, useEffect, type FC, type ReactNode } from "react";
import SystemMessages from "./components/SystemMessages";
import { SystemMessagesProvider } from "./contexts/SystemMessagesContext";
import { RunSelectionProvider } from "./hooks/runSelectionContext";
import { apolloClient, bindAuthenticatedFetch } from "./services/backend_gateway/apollo-client";
import { parquetWorkerManager } from "./utils/parquet-worker-manager";

/** Hands the shell's session transport to the Apollo singleton. Bound during
 *  render (not only in an effect): a child page's first query fires from its
 *  own effect, which runs before this component's would, and the fetch must
 *  already be the signed-in one by then. Bound again in the effect because
 *  StrictMode (dev) runs the cleanup once on mount and then re-runs effects,
 *  not renders — without the re-bind the singleton stayed unbound and every
 *  query failed "session is not ready". Idempotent, so re-renders are free. */
const ApolloSessionBridge: FC = () => {
  const { fetchWithToken } = useContext(AuthenticationContext);
  bindAuthenticatedFetch(fetchWithToken);
  useEffect(() => {
    bindAuthenticatedFetch(fetchWithToken);
    return () => bindAuthenticatedFetch(null);
  }, [fetchWithToken]);
  return null;
};

/**
 * LMNT's own providers around the routed app — what `product.wrap` hands the
 * shell (@atomic/app-shell). The shell owns auth, the frame and the dock; these
 * are the product's: the Apollo client every page queries through, the run
 * selection the Replenishment pages and the Nucleus dock share (the dock is the
 * shell's, mounted beside the page, so the provider sits above both), the
 * system-message toasts, and the parquet worker pool.
 */
export const LmntProviders: FC<{ children: ReactNode }> = ({ children }) => {
  // Warm the parquet worker POOL eagerly at app mount so each worker's WASM
  // fetch+compile starts immediately and overlaps auth + bundle parse —
  // finishing before the first mapping/source parquet parse needs it (ported
  // from dashmart; see MAX_PARQUET_WORKERS in parquet-worker-manager).
  useEffect(() => {
    parquetWorkerManager.warm();
    return () => {
      parquetWorkerManager.terminate();
    };
  }, []);

  return (
    <ApolloProvider client={apolloClient}>
      <ApolloSessionBridge />
      <RunSelectionProvider>
        <SystemMessagesProvider>
          {children}
          <SystemMessages />
        </SystemMessagesProvider>
      </RunSelectionProvider>
    </ApolloProvider>
  );
};
